import { useEffect, useState } from 'react';

import { getFileEntryPermissions } from '@/src/components/project/Inbox2/models/FileEntry';
import { mapInboxEntryWorkstreams } from '@/src/components/project/Inbox2/models/InboxEntry';
import { FileEntry } from '@/src/components/project/Inbox2/types';
import { getFileSize } from '@/src/util/files';

import { useEmailFileDetails } from './api/getEmailFileDetails';

const mapFileEntry = (rawFileEntry): FileEntry => {
  return {
    id: rawFileEntry.id,
    file: {
      id: rawFileEntry.document.id,
      name: rawFileEntry.document['file-origin'],
      rawName: rawFileEntry.document['file-name'],
      user: {
        id: rawFileEntry.created.userId,
        firstName: rawFileEntry.created[0]['name'],
        lastName: '',
      },
      folder: {
        id: rawFileEntry.document.folderId,
        location: rawFileEntry.document['file-location'],
      },
      libraryLocation: {
        id: rawFileEntry['library-location']?.id,
        path: rawFileEntry['library-location']?.['full-path'],
      },
    },
    source: rawFileEntry.source,
    status: rawFileEntry.status,
    from: {
      id: rawFileEntry.created.userId,
      emailAddress: rawFileEntry.from[0]['email-address'],
      fullName: rawFileEntry.from[0]['full-name'],
    },
    createdDate: rawFileEntry.created.date,
    openedDate: rawFileEntry.opened?.date,
    modifiedBy: rawFileEntry.modified?.name,
    modifiedDate: rawFileEntry.modified?.date,
    openedBy: rawFileEntry.opened?.name,
    closedDate: rawFileEntry.closed?.date,
    closedBy: rawFileEntry.closed?.name,
    workstreams: mapInboxEntryWorkstreams(rawFileEntry.workstreams),
    tasks: rawFileEntry.tasks,
    fileLocation: rawFileEntry.document['file-location'],
    fileSize: getFileSize(rawFileEntry.document['file-size-bytes']),
    accessList: rawFileEntry['access-list'],
    isMisc: rawFileEntry.misc,
    isRecorded: !!rawFileEntry.document.recorded,
    recordedDate: rawFileEntry.document.recorded?.date,
    recordedBy: rawFileEntry.document.recorded?.name,
    isRead: rawFileEntry.read,
    isArchivedInLibrary: false,
    allowedStatusChanges: rawFileEntry['allowed-status-changes'],
    allowClose: rawFileEntry['allow-close'],
    permissions: getFileEntryPermissions(rawFileEntry.permissions),
    shouldWaitSocketUpdate: rawFileEntry.shouldWaitSocketUpdate,
    isMultiPagePdf: rawFileEntry['is-multi-page-pdf'],
    duplicate: rawFileEntry.duplicate,
    tags: rawFileEntry.tags ?? [],
  };
};

type useMappeEmailEntryFileProps = {
  fileId: number;
  emailId: number;
  inboxEntryId: number;
  projectId: string;
};

export const useMappeEmailEntryFile = ({
  fileId,
  emailId,
  inboxEntryId,
  projectId,
}: useMappeEmailEntryFileProps) => {
  const fileDetailsMutation = useEmailFileDetails({
    projectId,
    inboxEntryId,
    emailId,
    fileId,
  });
  const [mappedFileEntry, setMappedFileEntry] = useState(null);

  useEffect(() => {
    if (!fileDetailsMutation.data) {
      return;
    }

    if (!fileDetailsMutation.data?.data) {
      return;
    }

    setMappedFileEntry(mapFileEntry(fileDetailsMutation.data.data));
  }, [fileDetailsMutation.data]);

  return {
    mappedFileEntry,
    isLoading: fileDetailsMutation.isLoading,
    fileDetailsQueryData: fileDetailsMutation.data,
    fileDetailsMutation: fileDetailsMutation,
  };
};
